import React, { useState } from 'react';
import {
  Box,
  CardActions,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DateTime } from 'luxon';
import { themeColors } from '../styles/colors';

import { IconAvatar, IconAvatarType } from '@act/storybook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { timeFormatter } from '@act/shared/utilities';
import { FlexContainer } from '@act/storybook';
import { SmartContentDisplay } from '@act/storybook/display/SmartContentDisplay/SmartContentDisplay';
import { ExpandableHeaderCardContent } from './ExpandableHeaderCardContent';
import { ExpandableHeaderToggleLabel } from './ExpandableHeaderToggleLabel';
import { ExpandMore } from './ExpandMore';
import { InfoCardContainer } from './InfoCardContainer';
import { InfoCardContent } from './InfoCardContent';
import { InfoCardHeader } from './InfoCardHeader';
import { InfoCardSubjectTitle } from './InfoCardSubjectTitle';
import type { BackgroundStyles } from './infoCard.types';
export type { BackgroundStyles } from './infoCard.types';
export { ExpandMore } from './ExpandMore';
export { InfoCardContainer } from './InfoCardContainer';
export { InfoCardContent } from './InfoCardContent';

export type FeedCardAction = {
  icon?: string | React.ReactNode;
  title: string | React.ReactNode;
  details?: string;
  handler: () => void;
};

export type AdditionalFeedCardActions = Pick<
  FeedCardAction,
  'title' | 'details' | 'handler' | 'icon'
>;

export interface InfoCardProps {
  children?: React.ReactElement;
  expandedContent?: React.ReactElement;
  expandableHeaderContent?: React.ReactElement;
  expandableHeaderLabel?: string;
  shouldHighlight?: boolean;
  menuItems?: AdditionalFeedCardActions[];
  subject: string | React.ReactElement;
  startTime: number;
  endTime?: number;
  timeDisplayOverride?: string | null;
  timeAsDue?: boolean;
  includeTime?: boolean;
  statusChips?: React.ReactElement[];
  content?: React.ReactElement;
  primaryOption?: React.ReactElement;
  secondaryOption?: React.ReactElement;
  additionalAction?: React.ReactElement;
  backgroundStyle?: BackgroundStyles;
  variant:
    | 'visit'
    | 'call'
    | 'message'
    | 'resource'
    | 'reminder'
    | 'note'
    | 'transfer'
    | 'task'
    | 'change'
    | 'hospitalization'
    | 'member-progressed'
    | 'appointment';
  subVariant?: 'incoming' | 'outgoing' | 'attended' | 'missed' | 'cancelled';
}

const InfoCard = ({
  children,
  expandedContent,
  expandableHeaderContent,
  expandableHeaderLabel,
  statusChips,
  subject,
  backgroundStyle = 'default',
  includeTime = true,
  timeAsDue = false,
  startTime,
  endTime,
  timeDisplayOverride,
  variant,
  subVariant,
  secondaryOption,
  primaryOption,
  additionalAction,
  menuItems,
  shouldHighlight,
}: InfoCardProps) => {
  const [expandedHeader, setExpandedHeader] = React.useState(false);

  const [additionalMenuAnchor, setAdditionalMenuAnchor] =
    useState<null | HTMLElement>(null);
  const open = Boolean(additionalMenuAnchor);
  const openAdditionalMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdditionalMenuAnchor(event.currentTarget);
  };
  const closeAdditionalMenu = () => {
    setAdditionalMenuAnchor(null);
  };
  function executeHandler(handler: () => void) {
    setAdditionalMenuAnchor(null);
    handler();
  }
  const handleExpandedHeaderClick = () => {
    setExpandedHeader(!expandedHeader);
  };

  const start = startTime ? DateTime.fromMillis(startTime) : null;
  let time: string = '';
  if (timeDisplayOverride != null && timeDisplayOverride.trim() !== '') {
    time = timeDisplayOverride.trim();
  } else if (startTime) {
    if (endTime) {
      time = timeFormatter.durationDateString(startTime, endTime);
    } else {
      time = timeFormatter.getDateString({ date: start, includeTime });
    }
  }

  if (timeAsDue && time !== '') {
    time = `Due ${time}`;
  }

  const showCardOptions =
    !!expandedContent || !!primaryOption || !!secondaryOption;

  function getAvatarType(variant: string, subVariant: string) {
    const infoType = ['call', 'appointment'].includes(variant)
      ? `${subVariant ? `${subVariant}_` : ''}${variant}`
      : variant;
    return infoType.replace(' ', '_').toLocaleLowerCase() as IconAvatarType;
  }

  return (
    <InfoCardContainer
      backgroundStyle={backgroundStyle}
      shouldHighlight={Boolean(shouldHighlight)}
    >
      <InfoCardHeader>
        <IconAvatar avatarType={getAvatarType(variant, subVariant)} />
        <FlexContainer
          direction="column"
          height="fit-content"
          minWidth="0"
          gap={0}
          overflowAllVisible
        >
          <FlexContainer
            direction="row"
            height="fit-content"
            gap={1}
            alignItems="center"
            flexWrap
          >
            <InfoCardSubjectTitle>{subject}</InfoCardSubjectTitle>
            {statusChips?.length > 0 && (
              <FlexContainer
                direction="row"
                height="fit-content"
                gap={1}
                flexWrap
              >
                {statusChips.map((chip, i) => {
                  return <React.Fragment key={`chip-${i}`}>{chip}</React.Fragment>;
                })}
              </FlexContainer>
            )}
          </FlexContainer>
          <Typography variant="bodySmall" component="p">
            {time}
          </Typography>
          {expandableHeaderContent && (
            <FlexContainer direction="row" height="fit-content">
              {expandableHeaderLabel && (
                <ExpandableHeaderToggleLabel onClick={handleExpandedHeaderClick}>
                  {expandableHeaderLabel}
                </ExpandableHeaderToggleLabel>
              )}
              <ExpandMore
                expand={expandedHeader}
                onClick={handleExpandedHeaderClick}
                aria-expanded={expandedHeader}
                aria-label="show more"
              >
                <Box
                  component={ExpandMoreIcon}
                  color={themeColors.onSurfaceVariant}
                  width={16}
                  height={16}
                />
              </ExpandMore>
            </FlexContainer>
          )}
        </FlexContainer>
        {menuItems?.length > 0 && (
          <Box alignSelf="start">
            <Box
              component={IconButton}
              margin="-8px"
              aria-label="settings"
              onClick={openAdditionalMenu}
            >
              <MoreVertIcon htmlColor={themeColors.onSurfaceVariant} />
            </Box>
            <Menu
              anchorEl={additionalMenuAnchor}
              open={open}
              onClose={closeAdditionalMenu}
            >
              {menuItems.map((item, index) => (
                <MenuItem
                  onClick={() => {
                    executeHandler(item.handler);
                  }}
                  key={`menu-item-${index}`}
                >
                  {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                  <ListItemText>{item.title}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </InfoCardHeader>

      {expandableHeaderContent && (
        <Collapse in={expandedHeader} timeout="auto" unmountOnExit>
          <ExpandableHeaderCardContent>
            {expandableHeaderContent}
          </ExpandableHeaderCardContent>
        </Collapse>
      )}

      {children && <InfoCardContent>{children}</InfoCardContent>}
      {expandedContent && (
        <InfoCardContent>
          <SmartContentDisplay>{expandedContent}</SmartContentDisplay>
        </InfoCardContent>
      )}
      {showCardOptions && (
        <Box display="grid" gap="8px" margin="0px" gridTemplateColumns="auto 1fr auto">
          <CardActions disableSpacing>
            {primaryOption}
            {secondaryOption}
          </CardActions>
          {additionalAction && (
            <FlexContainer direction="row" height="fit-content">
              {additionalAction}
            </FlexContainer>
          )}
        </Box>
      )}
    </InfoCardContainer>
  );
};

export default InfoCard;
